import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { utmPayload } from "@/lib/utm";
import { toast } from "sonner";

const GetStartedContext = createContext({ open: () => {}, close: () => {} });
export const useGetStarted = () => useContext(GetStartedContext);

const CHOICES = {
  service: { label: "What do you need?", options: ["New website", "Website redesign", "SEO / more leads", "E-commerce", "Something else"] },
  location: { label: "Where is your business?", options: ["Inland Empire", "Orange County", "Los Angeles County", "Elsewhere in SoCal", "Outside SoCal"] },
  budget: { label: "Budget", options: ["Under $5k", "$5k – $10k", "$10k – $25k", "$25k+", "Not sure yet"] },
  timeline: { label: "When do you want to start?", options: ["ASAP", "1–3 months", "3–6 months", "Just exploring"] },
};
const slug = (v) => v.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

export function GetStartedProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [prefill, setPrefill] = useState({});
  const open = useCallback((ctx) => {
    setPrefill(ctx && !ctx.nativeEvent ? ctx : {});
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, close]);

  return (
    <GetStartedContext.Provider value={{ open, close }}>
      {children}
      {isOpen && <GetStartedDialog onClose={close} prefill={prefill} />}
    </GetStartedContext.Provider>
  );
}

function GetStartedDialog({ onClose, prefill }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "", budget: "", message: "",
    service: "", location: prefill.location || "", timeline: "", industry: prefill.industry || "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };
  const update = (k) => (e) => setField(k, e.target.value);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please share your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "A valid email helps us reply.";
    for (const k of Object.keys(CHOICES)) if (!form[k]) e[k] = "Please pick one.";
    if (form.message.trim().length < 10) e.message = "A sentence or two, at least.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        budget: form.budget,
        service: form.service,
        location: form.location,
        timeline: form.timeline,
        industry: form.industry.trim() || null,
        page_path: window.location.pathname,
        message: form.message.trim(),
        ...utmPayload(),
      });
      setSent(true);
      // Close the dialog then route to the confirmation page so Google Ads
      // (and any other conversion tracker) can count this submission via URL.
      onClose();
      navigate("/confirmation?source=contact");
    } catch (err) {
      toast.error("Something went wrong. Please email hello@redkeyweb.design.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="get-started-title"
      data-testid="get-started-dialog"
      className="fixed inset-0 z-[100] flex items-start md:items-center justify-center overflow-y-auto p-4 sm:p-6"
    >
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
        data-testid="get-started-backdrop"
      />
      <div className="relative w-full md:max-w-3xl bg-paper border hairline shadow-xl my-auto flex flex-col max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-5rem)]">
        <div className="flex items-center justify-between px-6 md:px-10 py-4 border-b hairline shrink-0">
          <p className="eyebrow" id="get-started-eyebrow">Start a project</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="get-started-close"
            className="p-2 -mr-2 text-ink hover:text-electric transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 md:px-10 py-8 md:py-10 pb-12 md:pb-14 overflow-y-auto grow">
          {sent ? (
            <div data-testid="get-started-success">
              <h2 id="get-started-title" className="font-display text-3xl md:text-5xl">
                Thanks — message received.
              </h2>
              <p className="mt-4 text-ink-soft max-w-prose">
                We&rsquo;ll read this properly and reply from{" "}
                <span className="text-electric-ink">hello@redkeyweb.design</span> within{" "}
                <strong className="text-ink font-ui">1&ndash;2 business days.</strong>
              </p>
              <button
                type="button"
                onClick={onClose}
                data-testid="get-started-done"
                className="mt-8 inline-flex items-center gap-2 bg-ink text-paper px-5 py-3 font-ui hover:bg-electric transition-colors"
              >
                Close <span aria-hidden>→</span>
              </button>
            </div>
          ) : (
            <>
              <h2 id="get-started-title" className="font-display text-3xl md:text-5xl">
                Tell us about your project.
              </h2>
              <p className="mt-3 text-ink-soft max-w-prose">
                A few details and we&rsquo;ll be in touch within one business day.
              </p>

              <form onSubmit={onSubmit} noValidate data-testid="get-started-form" className="mt-8 space-y-8">
                <Field label="Your name" id="gs-name" testId="gs-name" value={form.name} onChange={update("name")} error={errors.name} autoComplete="name" />
                <Field label="Email" id="gs-email" testId="gs-email" type="email" value={form.email} onChange={update("email")} error={errors.email} autoComplete="email" />
                <Field label="Company (optional)" id="gs-company" testId="gs-company" value={form.company} onChange={update("company")} autoComplete="organization" />

                {Object.entries(CHOICES).map(([k, cfg]) => (
                  <ChipGroup key={k} name={k} label={cfg.label} options={cfg.options} value={form[k]} error={errors[k]}
                    onChange={(v) => setField(k, v)} />
                ))}
                <Field label="Industry (optional)" id="gs-industry" testId="gs-industry" value={form.industry} onChange={update("industry")} />

                <div>
                  <label htmlFor="gs-message" className="block form-label mb-3">
                    About your project
                  </label>
                  <textarea
                    id="gs-message"
                    data-testid="gs-message"
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    className="w-full bg-transparent border-b hairline-strong focus:border-electric outline-none py-3 text-base font-body resize-none"
                    placeholder="What are you building, who is it for, and where are you in the process?"
                  />
                  {errors.message && (
                    <p className="mt-2 text-sm text-[hsl(var(--destructive))]">{errors.message}</p>
                  )}
                </div>

                <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="gs-submit"
                    className="inline-flex items-center justify-between gap-6 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 disabled:opacity-60"
                  >
                    <span className="font-ui text-base">{submitting ? "Sending…" : "Send message"}</span>
                    <span aria-hidden>→</span>
                  </button>
                  <p className="font-ui text-[11px] text-muted-ink tracking-widest">
                    NO SPAM. NO MAILING LIST. JUST A REPLY.
                  </p>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ChipGroup({ name, label, options, value, onChange, error }) {
  return (
    <fieldset data-testid={`gs-group-${name}`}>
      <legend className="block form-label mb-3">{label}</legend>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
        {options.map((o) => {
          const active = value === o;
          return (
            <button type="button" key={o} role="radio" aria-checked={active}
              data-testid={`gs-${name}-${slug(o)}`}
              onClick={() => onChange(active ? "" : o)}
              className={["font-ui text-sm px-4 py-2 border transition-colors duration-200",
                active ? "border-ink bg-ink text-paper" : "hairline text-ink-soft hover:text-ink hover:border-ink"].join(" ")}>
              {o}
            </button>
          );
        })}
      </div>
      {error && <p className="mt-2 text-sm text-[hsl(var(--destructive))]" data-testid={`gs-error-${name}`}>{error}</p>}
    </fieldset>
  );
}

function Field({ label, id, testId, value, onChange, error, type = "text", autoComplete }) {
  return (
    <div>
      <label htmlFor={id} className="block form-label mb-3">
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        data-testid={testId}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b hairline-strong focus:border-electric outline-none py-3 text-base font-body"
      />
      {error && <p className="mt-2 text-sm text-[hsl(var(--destructive))]">{error}</p>}
    </div>
  );
}
